import { useCallback, useContext, useState } from 'react'

import { type SanityProductFragment } from '@data/sanity/queries/types/product'
import { AnalyticsEventName, getProductEventPayload } from '@lib/analytics'
import { AnalyticsContext } from '@lib/analytics-context'
import { CartContext } from '@lib/cart/context'
import { useAddItemsToCart } from '@lib/cart/hooks'
import { type CartVariantLineItem } from '@lib/cart/types'
import { LanguageContext } from '@lib/language-context'
import { useCombinedListingCartAttributes } from '@lib/product/combined-listing/cart'
import { createCombinedListingProduct } from '@lib/product/combined-listing/request'
import { ShopContext } from '@lib/shop-context'
import { parseShopifyGlobalId } from '@lib/shopify/helpers'
import { StringsContext } from '@lib/strings-context'

import Alert from '@components/alert'
import SimplePortableText from '@components/simple-portable-text'
import ProductAddToCart from './product-add-to-cart'
import ProductCounter from './product-counter'

interface CombinedListingProductActionsProps {
  product: SanityProductFragment
  productIds: number[]
  variantIds: number[]
  frameSize: string
  artworkSize?: string
  isEmptyFrame: boolean
}

const CombinedListingProductActions = ({
  product,
  productIds,
  variantIds,
  frameSize,
  artworkSize,
  isEmptyFrame,
}: CombinedListingProductActionsProps) => {
  const { triggerAnalyticsEvent } = useContext(AnalyticsContext)
  const { setIsCreatingProduct } = useContext(CartContext)
  const { locale } = useContext(LanguageContext)
  const strings = useContext(StringsContext)
  const { currencyCode } = useContext(ShopContext)

  const [quantity, setQuantity] = useState(1)
  const [isAddToCartError, setIsAddToCartError] = useState(false)

  const combinedListingCartAttributes = useCombinedListingCartAttributes()
  const addItemsToCart = useAddItemsToCart()

  const handleAddToCart = useCallback(async () => {
    setIsAddToCartError(false)

    let productTitle: string | undefined
    let variantId: number | undefined

    if (isEmptyFrame) {
      // Use product data directly, if empty frame
      productTitle = product.title
      variantId = variantIds[0]
    }

    if (!isEmptyFrame && variantIds.length > 1) {
      try {
        // Create combined listing product, if not empty frame
        setIsCreatingProduct(true)

        const combinedListingProduct = await createCombinedListingProduct(
          locale,
          productIds,
          frameSize,
          artworkSize,
        )
        productTitle = combinedListingProduct.productTitle
        variantId = parseShopifyGlobalId(combinedListingProduct.variantId)

        setIsCreatingProduct(false)
      } catch (error) {
        console.log(error)

        setIsAddToCartError(true)
        setIsCreatingProduct(false)

        return
      }
    }

    if (!variantId) {
      return
    }

    // Add combined listing product to cart
    const variantLineItems: CartVariantLineItem[] = [
      {
        id: variantId,
        quantity,
        attributes: combinedListingCartAttributes,
      },
    ]
    const isSuccessful = await addItemsToCart(variantLineItems)

    setIsAddToCartError(!isSuccessful)

    if (!isSuccessful) {
      return
    }

    if (productTitle && variantId) {
      const eventPayload = getProductEventPayload(
        productTitle,
        [
          {
            variantId,
            // TODO: Get variant options for `item_variant`.
            options: [],
            // TODO: Get variant price for `price`.
            price: 0,
            // TODO: Get variant compare price for `discount`.
            comparePrice: 0,
            quantity,
          },
        ],
        currencyCode,
      )
      triggerAnalyticsEvent(AnalyticsEventName.AddToCart, eventPayload)
    }
  }, [
    addItemsToCart,
    artworkSize,
    combinedListingCartAttributes,
    currencyCode,
    frameSize,
    isEmptyFrame,
    locale,
    product,
    productIds,
    quantity,
    setIsCreatingProduct,
    triggerAnalyticsEvent,
    variantIds,
  ])

  return (
    <div className="flex flex-col gap-y-5 items-baseline w-full text-center">
      <ProductCounter
        maximumProductCount={9999}
        onUpdate={(newQuantity) => setQuantity(newQuantity)}
      />

      <ProductAddToCart
        variant="filled"
        disabled={variantIds.length === 0}
        onClick={handleAddToCart}
        className="max-w-sm w-full"
      >
        {strings.buttonAddToCart}
      </ProductAddToCart>

      {isAddToCartError && (
        <div key="error">
          <Alert error>
            <SimplePortableText
              className="rc rc-alert rc-error"
              content={strings.cartAddToCartErrorMessage}
            />
          </Alert>
        </div>
      )}
    </div>
  )
}

export default CombinedListingProductActions
